import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { downloadFile } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { Ticket, TicketStatus, TicketPriority, Customer, User as AuthUser, Device } from "../types";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import TicketMergeBatchModal from "../components/TicketMergeBatchModal";
import { useVisibleColumnCount, Breakpoint } from "../hooks/useVisibleColumns";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { formatDateTime } from "../utils/datetime";
import { usePublishScreenData } from "../utils/aiScreenContext";

function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 0) totalSeconds = 0;
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function ActiveClock({ ticket, large = false }: { ticket: Ticket; large?: boolean }) {
  // Snapshot the value + when we received it; live-tick by (now - fetchedAt) when running.
  const fetchedAt = useRef(Date.now());
  useEffect(() => {
    fetchedAt.current = Date.now();
  }, [ticket.active_seconds, ticket.clock_running]);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!ticket.clock_running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [ticket.clock_running]);

  const elapsedSinceFetch = ticket.clock_running
    ? Math.floor((Date.now() - fetchedAt.current) / 1000)
    : 0;
  const total = ticket.active_seconds + elapsedSinceFetch;
  const text = formatDuration(total);

  if (!large) {
    return (
      <span className={`font-mono ${ticket.clock_running ? "text-emerald-600" : "text-gray-400"}`}>
        {text}
      </span>
    );
  }
  return (
    <div className="flex flex-col items-end">
      <span
        className={`font-mono font-bold text-lg ${ticket.clock_running ? "text-emerald-600" : "text-gray-400"}`}
      >
        {text}
      </span>
      <span className="text-[10px] uppercase text-gray-500 mt-0.5">
        {ticket.clock_running
          ? "Active · ticking"
          : ticket.waiting_on_customer
            ? "Paused · waiting on customer"
            : ticket.status === "on_hold"
              ? `Paused · ${ticket.hold_category_name || "on hold"}`
              : ticket.status === "resolved" || ticket.status === "closed"
                ? `Paused · ${ticket.status}`
                : "Paused"}
      </span>
    </div>
  );
}

export { ActiveClock, formatDuration };

const STATUS_COLORS: Record<TicketStatus, string> = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  on_hold: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  closed: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};
const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  medium: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolved: "Resolved",
  closed: "Closed",
};

const PRIORITY_RANK: Record<TicketPriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};
const STATUS_RANK: Record<TicketStatus, number> = {
  open: 0,
  in_progress: 1,
  on_hold: 2,
  resolved: 3,
  closed: 4,
};

export default function Tickets() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "technician";
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);

  const [filters, setFilters] = useState({
    customer_id: "" as string,
    assigned_to_user_id: "" as string,
    status: "" as string,
    priority: "" as string,
    search: "",
    include_closed: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_id: 0,
    device_id: 0,
    title: "",
    description: "",
    priority: "medium" as TicketPriority,
    assigned_to_user_id: 0,
    notify_email: "",
  });
  const [formError, setFormError] = useState("");
  // IN-FLIGHT GUARD for the create form.
  //
  // Tickets #520 and #521 were the same ticket, submitted twice 0.7s apart:
  // two POSTs from one browser, both 201 Created, and a technician left
  // merging them. Creating a ticket sends its notification mail inside the
  // request, so the button sits live and the form sits on screen for about a
  // second - which is plenty, and worse when the DB pool is busy (it was at
  // 18/20 that moment).
  //
  // A REF, not just state. setState is asynchronous: a second click landing
  // before React re-renders still reads the old value and fires. The ref
  // flips synchronously inside the handler, so the second call returns at the
  // top. The state exists only to disable the button and label it.
  const creating = useRef(false);
  const [isCreating, setIsCreating] = useState(false);
  const { currentCustomerId } = useCustomer();
  const sort = useSort("updated_at", "desc");
  // colSpan is an ATTRIBUTE - no media query reaches it. Order and
  // breakpoints MUST mirror the SortableTh list below.
  // Which rows are ticked, by id. A Set rather than an array: the row render
  // asks "is this one selected" once per row, and that should not be a scan.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showBatchMerge, setShowBatchMerge] = useState(false);
  const TICKET_COLUMNS: Breakpoint[] = [
    "base", // select checkbox — ALWAYS visible, so it is "base" not a breakpoint
    "sm",   // #
    "base", // Title
    "md",   // Customer
    "sm",   // Priority
    "base", // Status
    "lg",   // Assignee
    "lg",   // Active for
    "lg",   // Time
    "md",   // Updated
  ];
  const ticketCols = useVisibleColumnCount(TICKET_COLUMNS);

  const load = async () => {
    const params: any = {};
    if (filters.customer_id) params.customer_id = filters.customer_id;
    if (filters.assigned_to_user_id) params.assigned_to_user_id = filters.assigned_to_user_id;
    // "muted" is a synthetic dropdown value, not a real status — translate it.
    if (filters.status === "muted") {
      params.muted = true;
      params.include_closed = true;
    } else if (filters.status) {
      params.status = filters.status;
    }
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    if (filters.include_closed) params.include_closed = true;
    const r = await api.get("/api/v1/tickets", { params });
    setTickets(r.data);
  };

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    // Staff-wide assignee list, not the admin-only /users route — a technician
    // reading that one got a 403 and an empty "Assign to" dropdown.
    api
      .get("/api/v1/users/assignable")
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, []);
  // The global customer switcher drives this page's customer filter.
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      customer_id: currentCustomerId != null ? String(currentCustomerId) : "",
    }));
  }, [currentCustomerId]);
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [
    filters.customer_id,
    filters.assigned_to_user_id,
    filters.status,
    filters.priority,
    filters.include_closed,
  ]);

  // Keep the queue current without a manual reload. 30s while the tab is
  // visible, and immediately on coming back to it — a helpdesk queue that
  // only updates when you press F5 is not telling you a ticket arrived.
  // Paused while the create form is open: refetching under an operator who
  // is mid-sentence would re-render the list they are working against, and
  // nothing new is worth that.
  useAutoRefresh(load, 30000, !showForm);

  // Load devices for the selected customer in the create form
  useEffect(() => {
    if (!form.customer_id) {
      setDevices([]);
      return;
    }
    api
      .get("/api/v1/devices", { params: { customer_id: form.customer_id } })
      .then((r) => setDevices(r.data));
  }, [form.customer_id]);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    // Before validation: a double-click fires two identical submits, and both
    // would pass validation.
    if (creating.current) return;
    setFormError("");
    if (!form.customer_id) {
      setFormError("Pick a customer");
      return;
    }
    if (!form.title.trim()) {
      setFormError("Title is required");
      return;
    }
    creating.current = true;
    setIsCreating(true);
    try {
      const r = await api.post("/api/v1/tickets", {
        customer_id: form.customer_id,
        device_id: form.device_id || null,
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        assigned_to_user_id: form.assigned_to_user_id || null,
        notify_email: form.notify_email.trim() || null,
      });
      setShowForm(false);
      setForm({
        customer_id: 0,
        device_id: 0,
        title: "",
        description: "",
        priority: "medium",
        assigned_to_user_id: 0,
        notify_email: "",
      });
      navigate(`/tickets/${r.data.id}`);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || "Failed to create ticket");
    } finally {
      // IN `finally`, or a failed create locks the form out for good and the
      // only way back is a page reload - the technician retypes everything.
      // Same lesson as the agent's command worker pool: a slot that leaks on
      // failure is worse than no slot at all.
      creating.current = false;
      setIsCreating(false);
    }
  };

  const filtered = useMemo(() => {
    const term = filters.search.toLowerCase();
    if (!term) return tickets;
    return tickets.filter(
      (t) =>
        t.title.toLowerCase().includes(term) ||
        (t.description || "").toLowerCase().includes(term) ||
        String(t.id).includes(term),
    );
  }, [tickets, filters.search]);

  usePublishScreenData(
    "Tickets",
    filtered.map((t: any) => ({
      id: t.id,
      number: t.ticket_number,
      title: t.title,
      customer: t.customer_name,
      status: t.status,
      priority: t.priority,
      assignee: t.assigned_to_username,
      sla_due: t.sla_due_at,
    })),
  );

  const sortedTickets = sortRows(filtered, sort.sort, (t, key) => {
    switch (key) {
      case "title":
        return (t.title || "").toLowerCase();
      case "customer_name":
        return (t.customer_name || "").toLowerCase();
      case "priority":
        return PRIORITY_RANK[t.priority] ?? 0;
      case "status":
        return STATUS_RANK[t.status] ?? 0;
      case "assigned_to_username":
        return (t.assigned_to_username || "").toLowerCase();
      case "active_seconds":
        return t.active_seconds || 0;
      case "time_logged_minutes":
        return t.time_logged_minutes || 0;
      case "updated_at":
        return t.updated_at ? Date.parse(t.updated_at) : 0;
      default:
        return t.id;
    }
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Tickets</h1>
        <div className="flex items-center gap-2">
          {(() => {
            const params = () => {
              const p: any = {};
              if (filters.customer_id) p.customer_id = filters.customer_id;
              if (filters.assigned_to_user_id) p.assigned_to_user_id = filters.assigned_to_user_id;
              if (filters.status) p.status = filters.status;
              if (filters.priority) p.priority = filters.priority;
              if (filters.search) p.search = filters.search;
              p.include_closed = filters.include_closed;
              return p;
            };
            return (
              <>
                <button
                  onClick={() =>
                    downloadFile("/api/v1/exports/tickets.pdf", "tickets.pdf", params())
                  }
                  title="Download the tickets below as a formatted PDF report, honouring the filters currently applied"
                  className="border dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 inline-flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Export PDF
                </button>
                <button
                  onClick={() =>
                    downloadFile("/api/v1/exports/tickets.csv", "tickets.csv", params())
                  }
                  title="Download the tickets below as a CSV spreadsheet, honouring the filters currently applied"
                  className="border dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 inline-flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Export CSV
                </button>
              </>
            );
          })()}
          {canEdit && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              New Ticket
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        {/* Customer filter is driven by the global switcher in the header. */}
        <select
          value={filters.assigned_to_user_id}
          onChange={(e) => setFilters({ ...filters, assigned_to_user_id: e.target.value })}
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
        >
          <option value="">All assignees</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.username}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
        >
          <option value="">All statuses (open by default)</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
          <option value="muted">🔇 Muted</option>
        </select>
        <select
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
        >
          <option value="">All priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 ml-2">
          <input
            type="checkbox"
            checked={filters.include_closed}
            onChange={(e) => setFilters({ ...filters, include_closed: e.target.checked })}
          />
          Include resolved &amp; closed
        </label>
        <input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search title / description / #id"
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1 text-xs flex-1 min-w-[180px]"
        />
      </div>

      {/* Selection bar. Appears at two or more, because one ticket is not a
          merge. It is rendered above the table rather than floating, so it
          cannot cover the rows the user is still choosing between. */}
      {selected.size > 0 && (() => {
        const chosen = sortedTickets.filter((t: any) => selected.has(t.id));
        const customers = new Set(chosen.map((t: any) => t.customer_id ?? null));
        const mixed = customers.size > 1;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-3 px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-sm">
            <span className="font-medium text-blue-900 dark:text-blue-200">
              {selected.size} selected
            </span>
            {canEdit && selected.size >= 2 && !mixed && (
              <button
                onClick={() => setShowBatchMerge(true)}
                className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs"
                title="Merge these into one ticket — for when the same problem was reported more than once."
              >
                ⇄ Merge {selected.size} tickets
              </button>
            )}
            {canEdit && selected.size >= 2 && mixed && (
              // Not a disabled button: a greyed control with no reason is a
              // puzzle. Say what is wrong and it is self-service.
              <span className="text-xs text-blue-900/80 dark:text-blue-200/80">
                These are from different customers, so they cannot be merged.
                Deselect until only one customer is left.
              </span>
            )}
            {selected.size === 1 && (
              <span className="text-xs text-blue-900/80 dark:text-blue-200/80">
                Select another ticket to merge it with.
              </span>
            )}
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-xs text-blue-700 dark:text-blue-300 hover:underline"
            >
              Clear selection
            </button>
          </div>
        );
      })()}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              {/* Each `hide` must match its <td> below EXACTLY - a header
                  hidden while its cell is not shifts every later column. */}
              {/* Not a SortableTh: there is nothing to sort by, and it must
                  line up with the checkbox <td> added to every row below. */}
              <th className="px-3 py-2 w-8"></th>
              <SortableTh label="#" sortKey="id" sort={sort.sort} onSort={sort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Title" sortKey="title" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Customer" sortKey="customer_name" sort={sort.sort} onSort={sort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Priority" sortKey="priority" sort={sort.sort} onSort={sort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Status" sortKey="status" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Assignee" sortKey="assigned_to_username" sort={sort.sort} onSort={sort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Active for" sortKey="active_seconds" sort={sort.sort} onSort={sort.toggle} align="right" hide="hidden lg:table-cell" />
              <SortableTh label="Time" sortKey="time_logged_minutes" sort={sort.sort} onSort={sort.toggle} align="right" hide="hidden lg:table-cell" />
              <SortableTh label="Updated" sortKey="updated_at" sort={sort.sort} onSort={sort.toggle} hide="hidden md:table-cell" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedTickets.length === 0 && (
              <tr>
                <td colSpan={ticketCols} className="px-4 py-8 text-center text-gray-400">
                  No tickets match these filters.
                </td>
              </tr>
            )}
            {sortedTickets.map((t) => (
              <tr
                key={t.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                onClick={() => navigate(`/tickets/${t.id}`)}
              >
                {/* stopPropagation on BOTH handlers. The <tr> navigates on
                    click, so without this, ticking a box opens the ticket and
                    the selection is gone by the time you are back. */}
                <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ticket ${t.id}`}
                    checked={selected.has(t.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.id)) next.delete(t.id);
                        else next.add(t.id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-500 hidden sm:table-cell">#{t.id}</td>
                <td className="px-4 py-2 font-medium">
                  <Link to={`/tickets/${t.id}`} className="text-blue-600 hover:underline">
                    {t.title}
                  </Link>
                  {t.is_muted && (
                    <span
                      className="ml-2 text-xs text-gray-500"
                      title="Muted — customer notifications suppressed"
                    >
                      🔇
                    </span>
                  )}
                  {t.comment_count > 0 && (
                    <span className="ml-2 text-xs text-gray-400">💬 {t.comment_count}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300 hidden md:table-cell">{t.customer_name || "—"}</td>
                <td className="px-4 py-2 hidden sm:table-cell">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full capitalize ${PRIORITY_COLORS[t.priority]}`}
                  >
                    {t.priority}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status]}`}>
                    {STATUS_LABEL[t.status]}
                    {t.status === "on_hold" && t.hold_category_name && ` · ${t.hold_category_name}`}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300 hidden lg:table-cell">
                  {t.assigned_to_username || <span className="text-gray-400 dark:text-gray-500">Unassigned</span>}
                </td>
                <td className="px-4 py-2 text-right text-xs hidden lg:table-cell">
                  <ActiveClock ticket={t} />
                </td>
                <td className="px-4 py-2 text-right text-xs text-gray-500 hidden lg:table-cell">
                  {t.time_logged_minutes > 0
                    ? `${(t.time_logged_minutes / 60).toFixed(1)} hr`
                    : "—"}
                </td>
                <td className="px-4 py-2 text-xs text-gray-400 hidden md:table-cell">
                  {formatDateTime(t.updated_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">New Ticket</h2>
            <form onSubmit={submitCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Customer</label>
                <select
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm({ ...form, customer_id: Number(e.target.value), device_id: 0 })
                  }
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  required
                >
                  <option value={0}>Select…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  value={form.description}
                  rows={3}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Email to notify <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  value={form.notify_email}
                  onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
                  placeholder="user@customer.com"
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cc'd on creation + every status/priority change + every reply. Customer's contact
                  email and any portal users are notified too.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value as TicketPriority })
                    }
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Assignee</label>
                  <select
                    value={form.assigned_to_user_id}
                    onChange={(e) =>
                      setForm({ ...form, assigned_to_user_id: Number(e.target.value) })
                    }
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Device <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                  </label>
                  <select
                    value={form.device_id}
                    onChange={(e) => setForm({ ...form, device_id: Number(e.target.value) })}
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>None</option>
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.hostname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {formError && <p className="text-sm text-red-500">{formError}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm px-4 py-2 rounded-lg dark:hover:bg-gray-700/40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isCreating ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBatchMerge && (
        <TicketMergeBatchModal
          tickets={sortedTickets
            .filter((t: any) => selected.has(t.id))
            .map((t: any) => ({ id: t.id, title: t.title, status: t.status }))}
          onClose={() => setShowBatchMerge(false)}
          onMerged={(survivorId) => {
            setShowBatchMerge(false);
            // Drop the selection before reloading: the merged-away rows are
            // gone from the list, and a Set still holding their ids would keep
            // claiming "3 selected" over a list that now has one.
            setSelected(new Set());
            load();
            navigate(`/tickets/${survivorId}`);
          }}
        />
      )}
    </div>
  );
}
